/**
 * Section « Sessions utilisateurs ».
 *
 * Une session est une suite d'actions d'un même compte séparées par moins de
 * 10 minutes. Le tracker n'enregistre aucune fin de session : le découpage est
 * reconstruit, et le seuil est une convention.
 */

import { SectionShell } from '../components/SectionShell';
import { SessionDurationHistogram } from '../components/SessionDurationHistogram';
import { StatTile } from '../components/StatTile';
import { useAuth } from '../hooks/useAuth';
import { usePeriodParam } from '../hooks/usePeriodParam';
import { useSessionStats } from '../hooks/usePeriodStats';
import { formatDecimal, formatDuration, formatInteger, formatPercent } from '../lib/format';
import { SESSION_GAP_MINUTES } from '@shared/types/stats';

export function SessionsPage() {
  const { token, signOut } = useAuth();
  const [period] = usePeriodParam();

  const { data, error, isPending, isFetching } = useSessionStats({
    from: period.from,
    to: period.to,
    token,
    onUnauthorized: signOut,
  });

  const summary = data?.summary;

  return (
    <SectionShell
      lastSyncAt={data?.meta.lastSyncAt}
      isPending={isPending}
      isFetching={isFetching}
      error={error}
      hasData={Boolean(data)}
    >
      <div className="kpi-row">
        <StatTile
          label="Sessions"
          value={formatInteger(summary?.totalSessions ?? 0)}
          hint={`${formatInteger(summary?.usersWithSessions ?? 0)} utilisateurs`}
        />
        <StatTile
          label="Durée médiane"
          value={formatDuration(summary?.medianDurationSeconds ?? 0)}
          hint={`moyenne ${formatDuration(summary?.avgDurationSeconds ?? 0)}`}
        />
        <StatTile
          label="Actions par session"
          value={formatDecimal(summary?.medianActionsPerSession ?? 0)}
          hint={`médiane — moyenne ${formatDecimal(summary?.avgActionsPerSession ?? 0)}`}
        />
        <StatTile
          label="Sessions par utilisateur"
          value={formatDecimal(summary?.avgSessionsPerUser ?? 0)}
          hint="moyenne sur la période"
        />
        <StatTile
          label="Sessions d'une action"
          value={formatPercent(summary?.singleActionShare ?? 0)}
          hint="durée mesurée à 0 s — voir la note"
        />
      </div>

      <div className="chart-grid">
        <div className="chart-grid__wide">
          <SessionDurationHistogram
            data={data?.durationHistogram ?? []}
            meanSeconds={summary?.avgDurationSeconds ?? 0}
            medianSeconds={summary?.medianDurationSeconds ?? 0}
          />
        </div>
      </div>

      <p className="footnote">
        Une session regroupe les actions d'un même compte séparées de moins de{' '}
        {SESSION_GAP_MINUTES} minutes. Sa durée est l'écart entre sa première et sa dernière
        action : une session d'une seule action mesure donc 0 seconde, alors que la personne a
        bien joué. C'est une sous-estimation structurelle — le tracker n'enregistre pas de fin
        de session — et la tuile « sessions d'une action » en donne l'ampleur. Les comptes de
        test (identifiants 1 à 4) sont exclus.
      </p>
    </SectionShell>
  );
}
